import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridApi } from 'ag-grid-community';
import { Box, Typography } from '@mui/material';
import { StatusSelectorOptions } from 'components/SharedComponents/StatusSelector/StatusSelector';
import {
    PayComponentMapping,
    WageTypeSource,
} from 'routes/Mappings/components/PayComponentsTable/helpers/definitions';
import MappingValueSelector from 'routes/HumanResourcesModule/Settings/HrPayComponentMapping/components/MappingValueSelector';
import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
import {
    useUpdatePayComponentDestination,
    useUpdatePayComponentSources,
} from 'routes/Mappings/components/PayComponentsTable/hooks/payComponents';
import { EXCLUDED_VALUE } from 'business/constants';

type Props = {
    selectedPayComponentMappingsIds: number[];
    wageTypeMappings: WageTypeSource[];
    gridApi: GridApi;
    payComponentMapping: PayComponentMapping[];
};
export default function PayComponentMappingFieldHeaderSelector({
    selectedPayComponentMappingsIds,
    gridApi,
    payComponentMapping,
    wageTypeMappings,
}: Props) {
    const { t } = useTranslation();
    const updatePayComponentDestinationsMutation =
        useUpdatePayComponentDestination(true);
    const updatePayComponentSourcesMutation =
        useUpdatePayComponentSources(true);

    const handleChange = useCallback(
        (e: StatusSelectorOptions<number>) => {
            const newValue = e.value as unknown as number;
            const selectedPayComponent = payComponentMapping.find(
                (item) => item.destinationId === newValue,
            );
            // if the user selects the default option and a mapping already exist, we remove the source from the destination
            if (newValue === -1) {
                for (const item of selectedPayComponentMappingsIds) {
                    updatePayComponentDestinationsMutation.mutate({
                        sourceId: item,
                        destinationId: null,
                    });
                }
            } else {
                updatePayComponentSourcesMutation.mutate({
                    destinationId: newValue,
                    sourceIds: [
                        ...(selectedPayComponent?.linkedWageTypeSources.map(
                            (item) => item.sourceId,
                        ) || []),
                        ...selectedPayComponentMappingsIds,
                    ],
                });
            }

            trackEventPRH(
                TRACKABLE_EVENTS.human_resources.settings.selectPayComponent,
                {
                    wageType: wageTypeMappings
                        .filter((item) =>
                            selectedPayComponentMappingsIds.includes(
                                item.sourceId,
                            ),
                        )
                        .map((item) => item.code)
                        .join(', '),
                    payComponent: selectedPayComponent?.value,
                },
            );

            gridApi.deselectAll();
        },
        [
            selectedPayComponentMappingsIds,
            wageTypeMappings,
            payComponentMapping,
            updatePayComponentDestinationsMutation,
        ],
    );

    const formattedPayComponentData = useMemo(
        () =>
            payComponentMapping
                ? [
                      {
                          destinationId: -1,
                          value: t('unmapped'),
                          linkedWageTypeSources: [],
                      } as PayComponentMapping,
                      ...payComponentMapping.filter(
                          (row) => row.value !== EXCLUDED_VALUE,
                      ),
                  ]
                : [],
        [payComponentMapping],
    );

    return (
        <Box sx={{ flexDirection: 'row' }}>
            <Typography>{t('humanResources.settings.payComponent')}</Typography>
            <MappingValueSelector
                handleChange={handleChange}
                choices={formattedPayComponentData}
            />
        </Box>
    );
}
